import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, useBCMSContext, useRoute, useStateManager } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import UIRadioGroup, { UIRadio } from "components/custom-ui/UIRadioGroup";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import { PrimaryButton, SecondaryButton, UITextInput } from "components/ui";
import Classes, { PositivePayCheckFile, PositivePayCheckFileRow, PositivePayCheckTemplate } from "model";
import React from "react";
import { SVGAsset } from "res";
import TASPositivePayCreateCheckTemplateForm from "./classes/TASPositivePayCreateCheckTemplateForm";
import TASPositivePayCreateTemplateFileExcludeWidget from "./components/TASPositivePayCreateTemplateFileExcludeWidget";
import TASPositivePayCreateTemplateFileFormatWidget from "./components/TASPositivePayCreateTemplateFileFormatWidget";
import TASPositivePayCreateTemplateFileMapColumnsWidget from "./components/TASPositivePayCreateTemplateFileMapColumnsWidget";
import TASPositivePayCreateTemplateFileMappingPreviewWidget from "./components/TASPositivePayCreateTemplateFileMappingPreviewWidget";
import TASPositivePayCreateTemplateFilePreviewWidget from "./components/TASPositivePayCreateTemplateFilePreviewWidget";
import TASPositivePayCreateTemplateItemTypeMappingWidget from "./components/TASPositivePayCreateTemplateItemTypeMappingWidget";
import "./styles.css";
import "./mobile.css";


/**
 * @function TASPositivePayCreateTemplatePage
 * @return {React.Component}
 * @exported true
 * @description The main component for the TASPositivePayCreateTemplatePage.
 */
export default function TASPositivePayCreateTemplatePage(props) {
    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);
    const navigate = useRoute();

    const data = { ...getDefaultData(), ...props.data ?? {} };
    const ui = { ...getDefaultUI(), ...props.ui ?? {} };

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,
        onChangeCheckTemp: prop => value => {

            if(prop === "positivePayCheckFile") {
                value = PositivePayCheckFile.initByID("0")
            }

            BCMSStateManagerCRUD.init(state)
            .update(["data", "checkTemplate", prop], value)
            .submit(setState);
        },
        onChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", "template", prop], value)
                .submit(setState);
        },
        onDataChange: prop => value => {
            BCMSStateManagerCRUD.init(state)
                .update(["data", prop], value)
                .submit(setState);
        },
        onColumnChange: (id) => value => {
            // Map "mapColumn1" to id "0" since that is the indexing used in the "selectedColumns" array.
            var selectedMapColumnID = String(parseInt(id) + 1);
            var selectedMapColumnValue = value.value;
            var selectedMapColumnKey = `mapColumn${ selectedMapColumnID }`;

            const selectedColumns = data.selectedColumns.reduce((acc, item) => {
                if (item.id === id) {
                    item.value = value;
                    item.value.isSelected = true;
                }
                return [...acc, item]
            }, []);

            const mapColumns = data.mapColumns.reduce((acc, item) => {
                const selected = selectedColumns.find(column => column.value.id === item.id);

                if (item.isSelected !== undefined) {
                    item.isSelected = selected !== undefined ? true : false;
                }

                return [...acc, item]
            }, []);

            BCMSStateManagerCRUD.init(state)
                .update(["data", "selectedColumns"], selectedColumns)
                .update(["data", "mapColumns"], mapColumns)
                .update(["data", "checkTemplate", selectedMapColumnKey ], selectedMapColumnValue)
                .submit(setState);
        },
        onCancel: () => {
            navigate("/tasPositivePayImportChecks");
            window.scrollTo(0, 0);
        },
        onAdd: () => {
            const defaultCheckTemplate = BCMSDefaultModel.positivePayCheckTemplate();
            const mergedCheckTemplate = BCMSDefaultModel.mergeRecords(state.data.checkTemplate.record, defaultCheckTemplate.record);
            const nextID = PositivePayCheckTemplate.nextID();
            const newCheckTemplate = PositivePayCheckTemplate.init({ ...mergedCheckTemplate, id: nextID });
            const updates = [PositivePayCheckTemplate.create(newCheckTemplate)];
            BCMSRecord.batch(updates, context, Classes);
            navigate("/tasPositivePayImportChecks", { state: { newTemplate: newCheckTemplate } });
            window.scrollTo(0, 0);
        }
    });
    return (
        <PageContainer className="tas-positive-pay-create-template-page" state={{ data, ui }} controller={controller}>
            <ImportFileNewTemplatePageContent />
        </PageContainer>
    )
}

export const getDefaultData = () => {
    const checkTemplate = TASPositivePayCreateCheckTemplateForm.init();
    
    var selectedColumns = [
        { id: "0", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "1", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "2", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "3", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "4", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "5", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "6", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
        { id: "7", value: { id: "0", value: "(Exclude Column)", class: "placeholder-item" } },
    ];

    var mapColumns = [
        { id: "0", value: "(Exclude Column)", class: "placeholder-item" },
        { id: "1", value: "Payee", class: "payee" },
        { id: "2", value: "Amount", class: "amount", isSelected: false },
        { id: "3", value: "Account Number", class: "account-number", isSelected: false },
        { id: "4", value: "Item Type Code", class: "item-type-code", isSelected: false },
        { id: "5", value: "Account Type", class: "account-type", isSelected: false },
        { id: "6", value: "Reference Number", class: "reference-number", isSelected: false },
        { id: "7", value: "Check Number (Required)", class: "check-number", isSelected: false },
        { id: "8", value: "Issued Date", class: "issued-date", isSelected: false },
    ];

    const getMapColumn = value => {
        return mapColumns.filter(item => item.value === value)[0];
    };

    // This object will update "selectedColumns" and "mapColumns"
    // so that we can load in stored values from the model or from a preexisting template.
    Object.keys(checkTemplate).map(key => {
        if(key.includes("mapColumn")) {
            var mapColumnValue = checkTemplate[key];

            // Map "mapColumn1" to id "0" since that is the indexing used in the "selectedColumns" array.
            var selectedColumnIDInt = parseInt(key.replace("_mapColumn", ""));
            var selectedColumnID = String(selectedColumnIDInt - 1);

            selectedColumns.reduce((acc, column) => {
                if(column.id === selectedColumnID) {
                    if(mapColumnValue !== "(Exclude Column)") {
                        column.value = getMapColumn(mapColumnValue);
                        // The "mapColumns" object above will be updated by reference.
                        column.value.isSelected = true;
                    }
                }
                acc.push(column);
                return acc;
            }, []);
        }
        return key;
    });

    return {
        checkTemplate: TASPositivePayCreateCheckTemplateForm.init(),
        checkTemplateRows: PositivePayCheckFileRow.initAll().filter(item => {
            return item.positivePayCheckFile.id === "0";
        }),
        selectedColumns: selectedColumns,
        mapColumns: mapColumns,
    };
};

export const getDefaultUI = () => ({
    mapColumnSelect1: { isActive: false },
    mapColumnSelect2: { isActive: false },
    mapColumnSelect3: { isActive: false },
    mapColumnSelect4: { isActive: false },
    mapColumnSelect5: { isActive: false },
    mapColumnSelect6: { isActive: false },
    mapColumnSelect7: { isActive: false },
    mapColumnSelect8: { isActive: false },
});


/**
 * @function ImportFileNewTemplatePageContent
 * @return {React.Component}
 * @exported true
 * @description The page content component for the ImportFileNewTemplatePage.
 */
function ImportFileNewTemplatePageContent(props) {
    const { ui, data, onChangeCheckTemp, onCancel, onAdd } = useStateManager();
    const chosenFile = data.checkTemplate.positivePayCheckFile ? data.checkTemplate.positivePayCheckFile.description : `No file chosen`;
    const checkTemplate = data.checkTemplate;
    const didSelectItemTypeCode = data.selectedColumns.filter(item => item.value.value === "Item Type Code").length > 0;

    return (
        <PageContentView>
            <CenterContentContainer>
                <ContentHeaderView text="Create Template" />
                <FormContainer className="small-form-width">
                    <FormRowView text="File">
                        <div className="flex-container flex-horizontal align-center choose-file-container">
                            <div className="set-width secondary-button-container">
                                <SecondaryButton text="Choose File" onClick={ onChangeCheckTemp("positivePayCheckFile") } />
                            </div>
                            <p className="set-width">{ chosenFile }</p>
                        </div>
                    </FormRowView>
                    <FormRowView>
                        <p>Maximum file size: 500 KB</p>
                        <p>Valid file extensions: .csv, .txt</p>
                    </FormRowView>
                    <FormRowView className="import-attached-file-row" text="Import Attached File">
                        <UIRadioGroup value={ checkTemplate.importAttachedFile }>
                                <UIRadio className="ui-group-item set-width" label="Yes" value="true" />
                            <DisabledPrompt>
                                <UIRadio className="ui-group-item set-width" label="No" value="false" />
                            </DisabledPrompt>
                        </UIRadioGroup>
                    </FormRowView>
                    <FormRowView text="Template Name">
                        <UITextInput dataPoint={ `Template_Name_Input` } value={ checkTemplate.name } onChange={ onChangeCheckTemp("name") } />
                    </FormRowView>
                    <FormRowView className="input-decimals-into-amounts-row" text={
                        <div className="flex-container flex-horizontal align-center">
                            <p className="label-with-icon">Input Decimals into Amount</p>
                            <SVGAsset id="InfoIcon" />
                        </div>
                    }>
                        <DisabledPrompt>
                            <CustomSelect dataPoint={ `Input_Decimals_Into_Amounts_Select` } 
                                value={ checkTemplate.inputDecimalsIntoAmount } onChange={ onChangeCheckTemp("inputDecimalsIntoAmount") }>
                                <CustomSelectItem dataPoint={ `Input_Decimals_Into_Amounts_Option_${ `Yes` }` } value="yes">Yes</CustomSelectItem>
                                <CustomSelectItem dataPoint={ `Input_Decimals_Into_Amounts_Option_${ `No` }` } value="no">No</CustomSelectItem>
                            </CustomSelect>
                        </DisabledPrompt>
                    </FormRowView>
                    <TASPositivePayCreateTemplateFileExcludeWidget/>
                </FormContainer>
                <TASPositivePayCreateTemplateFileFormatWidget/>
                <TASPositivePayCreateTemplateFilePreviewWidget/>
                <TASPositivePayCreateTemplateFileMapColumnsWidget/>
                { didSelectItemTypeCode && <TASPositivePayCreateTemplateItemTypeMappingWidget/> }
                <TASPositivePayCreateTemplateFileMappingPreviewWidget/>

                <div className="button-container flex-container flex-horizontal">
                    <SecondaryButton dataPoint={ `Cancel_Button` } text="Cancel" onClick={ onCancel } />
                    <PrimaryButton dataPoint={ `Add_Button` } text="Add" onClick={ onAdd } />
                </div>
            </CenterContentContainer>
        </PageContentView>
    )
}